import React, { useState, useEffect, useRef, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import styles from './HierarchicalFacetValueBrowser.module.css';
import { CC, ccConfig} from '../CC/CC'; 
import { generateQuery } from '../CC/utils'; 
import { CCProvider, useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import * as ccUtils from '../CC/utils';

interface FacetProps {
  hierarchicalFacetName: string;
  topCN: string;
  sort?: 'alpha' | 'count'; 
  onSelect?: (values: string[]) => void;
  showSelectedAbove?: boolean;
  isOpen: boolean;
  onClose?: () => void;
}

interface FacetDataItem {
  value: string; // This will be the CN
  term: string;  // This will be the actual term
  count: string;
}

const HierarchicalFacetValueBrowser: React.FC<FacetProps> = ({ hierarchicalFacetName, topCN, sort = 'count', onSelect = undefined, showSelectedAbove = true, isOpen = false, onClose = () => {} }) => {
  const { t } = useTranslation();  
  // /Record/ThesTerm/TermCN:1-200:regexp(^(?=.*utrec.*).*MQX.*)
  let field="/Record/ThesTerm/TermCN";
  const instanceRef = useRef({});
  const { user, cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData } = useCCContext();
  const ctx = useCCContext();
  const [currentQuery, setCurrentQuery] = useState("");
  const [selectedValues, setSelectedValues] = useState<{ CN: string; term: string }[]>(ctx.cc.hierarchicalFacets[hierarchicalFacetName] || []);
  const [renderCount, setRenderCount] = useState(0); // Add this line at the beginning of your component
  const [facetData, setFacetData] = useState<FacetDataItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const observer = useRef<IntersectionObserver | null>(null);
  const [userInput, setUserInput] = useState('');
  const [currentSort, setCurrentSort] = useState(sort);
  const [facetCount, setFacetCount] = useState(0);
  const facetRootRef = useRef<HTMLDivElement>(null);
  
  let hierarchicalFacetLabel = "";
  if (ccConfig.hierarchicalFacets[hierarchicalFacetName]) {
    hierarchicalFacetLabel = ccConfig.hierarchicalFacets[hierarchicalFacetName].label;
  }
  
  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        console.log('setting page: '+page);
        setPage(prevPage => prevPage + 1);
        forceUpdate();
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore]);

  const fetchData = async (page: number) => {
    const pageSize = 100;
    const query = generateQuery(cc, user, { excludeFacet: hierarchicalFacetName }); 
    setCurrentQuery(query);
  
    const postData = {
      action: "get",
      command: "facets",
      fields: `${field}:${pageSize * (page - 1) + 1}-${pageSize * page}:regexp(${createRegex(userInput, topCN)})`,
      query: query,
      sort: currentSort,
      responseformat: "json"
    };
  
    try {
      const data = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, postData, instanceRef);
      
      let newData: FacetDataItem[] = Array.isArray(data.facets.facet.value) 
      ? data.facets.facet.value 
      : [data.facets.facet.value];
  
      // Split values into term and CN
      newData = newData.map(item => {
          const lastSpaceIndex = item.value.lastIndexOf(' ');
          const term = item.value.substring(0, lastSpaceIndex);
          const value = item.value.substring(lastSpaceIndex + 1);
          return {
              ...item,
              term,
              value
          };
      });
  
      setFacetData(prev => {
          const updatedData = [...prev];
          newData.forEach(item => {
              if (!updatedData.find(i => i.value === item.value)) {
                  updatedData.push(item);
              }
          });
          return updatedData;
      });
  
      setHasMore(data.facets.facet.count > pageSize * page);
      setFacetCount(data.facets.facet.count);
    } catch (error) {
      setFacetCount(0);
      console.error(error);
    }
  };  

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserInput(e.target.value);
    setFacetData([]);
    setPage(1);
    forceUpdate();
  };

  const clearInput = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setUserInput('');
    setFacetData([]);
    setPage(1);
    forceUpdate();
  };

  const handleAlphaSort = () => {
    setCurrentSort('alpha');
    setFacetData([]);
    setPage(1);
    forceUpdate();
  };

  const handleCountSort = () => {
    setCurrentSort('count');
    setFacetData([]);
    setPage(1);
    forceUpdate();
  };

  const handleCheckboxChange = (checked: boolean, value: string) => {
    const facetItem = facetData.find(item => item.value === value);
    const selectedFacet = { CN: value, term: facetItem?.term || "" };

    if (checked) {
        setSelectedValues(prev => [...prev, selectedFacet]);
    } else {
        setSelectedValues(prev => prev.filter(item => item.CN !== value));
    }
};

  const forceUpdate = () => setRenderCount(renderCount + 1);

  useEffect(() => {
    fetchData(page);
  }, [page, renderCount]);
 
  useEffect(() => {
    const query = generateQuery(cc, user, { excludeFacet: hierarchicalFacetName }); 
    if(currentQuery != query) {
        setFacetData([]);
        setPage(1);
        forceUpdate();
        //fetchData(1);
    }
  }, [cc]);

  useEffect(() => {
    // Set selectedValues with the full facet objects from the context
    if (ctx.cc.hierarchicalFacets[hierarchicalFacetName]) {
        setSelectedValues(ctx.cc.hierarchicalFacets[hierarchicalFacetName]);
    } else {
        setSelectedValues([]);
    }
}, [ctx.cc.hierarchicalFacets, hierarchicalFacetName]);

  const handleSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    ctx.setCc(prev => ({
        ...prev,
        hierarchicalFacets: {
            ...prev.hierarchicalFacets,
            [hierarchicalFacetName]: selectedValues
        }
    }));

    if(onSelect) {
        onSelect(selectedValues.map(value => value.CN));
    }    

    onClose();
};


  const handleCancel = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    onClose();
  };  

  function createRegex(text: string, CN: string): string {
    // Split the text based on word separators like space, dash, etc.
    let words = text.split(/[\s-]+/);

    // Create a positive lookahead for each word
    let lookaheads = words.map(word => `(?=.*${word})`).join('');

    // Return the complete regex pattern
    return `^${lookaheads}.*${CN}.*$`;
  }

  return (
    isOpen ? (
      <div className={styles.HierarchicalFacetValueBrowser}>
        <div className={styles.popupoverlay} ></div>
        <div className={`${styles.popup} parentModalClass`}>

          <div className={styles.container}>

            <div className={styles.facetName}>
                <span className={styles.facetNameLabel}>
                  {t(hierarchicalFacetLabel)}
                </span>
                <span className={styles.facetNameCount}>
                  ({facetCount} {facetCount != 1 ? t("facet_values") : t("facet_value")})
                </span>
            </div>

            <div className={styles.facetContainer} ref={facetRootRef}>
              <div className={styles.header}>
                <div className={styles.inputContainer}>
                  <input className={styles.mask} type="text" value={userInput} placeholder={t("facet_filter")} onChange={handleInput} />
                </div>
                <GenericIconButton icon='light/times.svg' width='15px' height='15px' fill='#383838' hoverBackgroundColor='#f0f0f0' onClick={clearInput} />
                <div className={styles.sortButtons}>
                  <GenericIconButton isActive={currentSort === 'alpha'} activeBorderColor='#383838' icon='regular/sort-alpha-down.svg' width='15px' height='15px' fill='#383838' hoverFill='#0000ff' hoverBackgroundColor='#f0f0f0' onClick={handleAlphaSort} />
                  <GenericIconButton isActive={currentSort === 'count'} activeBorderColor='#383838' icon='regular/sort-amount-down.svg' width='15px' height='15px' fill='#383838' hoverFill='#0000ff' hoverBackgroundColor='#f0f0f0' onClick={handleCountSort} />
                </div>
              </div>
              <div className={styles.selectedItems}>
                {/*showSelectedAbove && cc.facets[facetName]?.length > 0 && (
                  <div className={styles.clearAllContainer} onClick={() => { ccUtils.clearFacet(ctx, facetName) }}>
                    <div className={styles.clearAllIconContainer}>
                      <div className={styles.clearAll} />
                      <div className={styles.clearAll} />
                      <div className={styles.clearAll} />
                    </div>
                    <div className={styles.clearAllText}>
                      Clear selected
                    </div>
                  </div>
                )*/}

                {/*showSelectedAbove && cc.facets[facetName]?.map((selectedValue) => (
                  <div key={selectedValue}>
                    <GenericCheckbox
                      id={`facet_${field}_${selectedValue}`}
                      name={`facet_${field}_${selectedValue}`}
                      value={selectedValue}
                      checked={true}
                      onChange={handleCheckboxChange}
                      label={selectedValue}
                    />
                  </div>
                ))*/}
              </div>

              <div className={styles.content}>
              {
                facetData.map((item) => {
                    return (
                        <div key={item.value} ref={facetData.indexOf(item) === facetData.length - 1 ? lastElementRef : null}>
                            <GenericCheckbox
                                id={`facet_${field}_${item.value}`}
                                name={`facet_${field}_${item.value}`}
                                value={item.value}
                                checked={selectedValues.some(selectedItem => selectedItem.CN === item.value)}
                                onChange={handleCheckboxChange}
                                label={`${item.term} (${item.count})`}
                            />
                        </div>
                    );
                })
              }
              </div>

            </div>

            <div className={styles.footer}>
              <button className={genericStyles.GenericButton} onClick={handleSearch}>
                {t("browse_apply")}
              </button>
              <button className={genericStyles.GenericButton} onClick={handleCancel}>{t("browse_cancel")}</button>
            </div>


          </div>

        </div>
      </div>
    ) : null
  )

}

export default HierarchicalFacetValueBrowser;
